import React from "react";
import { Table } from "react-bootstrap";
import { comparePoints } from "../../utilities";
import { getAvatarColor, getInitials } from "../../utilities/avatar";

import "./LeaderboardSidebar.css";

// Avatar size for leaderboard rows (smaller than lobby bubbles)
const LB_AVATAR_SIZE = 28;

/**
 * Small avatar for leaderboard row - shows photo or initials fallback.
 */
function PlayerAvatar({ player }) {
    const { nickname, avatarThumb } = player;
    const initials = getInitials(nickname);
    const bgColor = getAvatarColor(nickname);

    const style = {
        width: LB_AVATAR_SIZE,
        height: LB_AVATAR_SIZE,
        minWidth: LB_AVATAR_SIZE,
        backgroundColor: avatarThumb ? "transparent" : bgColor
    };

    if (avatarThumb) {
        return (
            <div className="lb-avatar" style={style}>
                <img src={avatarThumb} alt={nickname} />
            </div>
        );
    }

    return (
        <div className="lb-avatar lb-avatar-fallback" style={style}>
            <span className="lb-avatar-initials">{initials}</span>
        </div>
    );
}

/**
 * LeaderboardSidebar - Compact live leaderboard for host question view.
 *
 * Props:
 * - data: Array of player objects with { nickname, points, playerId, status? }
 * - title: Header text (default: "Leaderboard")
 * - showStatus: If true and players have status field, show Status column (knockout-ready)
 */
function LeaderboardSidebar({ data, title = "Leaderboard", showStatus = false }) {
    if (!data || data.length === 0) {
        return (
            <div className="leaderboard-sidebar">
                <div className="leaderboard-sidebar-header">{title}</div>
                <div className="leaderboard-sidebar-empty">
                    No players yet
                </div>
            </div>
        );
    }

    // Sort by points (descending), then by duration (ascending) for tie-breaker
    const sorted = data.slice(0).sort(comparePoints);

    // Determine if we should show status column:
    // Only if showStatus prop is true AND at least one player has a status field
    const hasStatusData = sorted.some(player => player.status !== undefined);
    const displayStatus = showStatus && hasStatusData;

    return (
        <div className="leaderboard-sidebar">
            <div className="leaderboard-sidebar-header">{title}</div>
            <div className="leaderboard-sidebar-content">
                <Table className={`leaderboard-sidebar-table ${displayStatus ? 'with-status' : ''}`}>
                    <thead>
                        <tr>
                            <th className="rank-col">#</th>
                            <th className="name-col">Player</th>
                            <th className="score-col">Pts</th>
                            {displayStatus && <th className="status-col">Status</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((player, index) => (
                            <tr
                                key={player.playerId || player.nickname || index}
                                className={player.status === 'out' ? 'player-out' : ''}
                            >
                                <td className="rank-col">{index + 1}</td>
                                <td className="name-col">
                                    <div className="lb-name-wrap">
                                        <PlayerAvatar player={player} />
                                        <span className="lb-name">{player.nickname}</span>
                                    </div>
                                </td>
                                <td className="score-col">{player.points}</td>
                                {displayStatus && (
                                    <td className={`status-col status-${player.status || 'active'}`}>
                                        {player.status === 'out' ? 'Out' : 'In'}
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>
        </div>
    );
}

export default LeaderboardSidebar;
